/* eslint-disable @nx/enforce-module-boundaries */
import { inject, Injectable } from '@angular/core';
import { catchError, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { User } from '../user/user.model';
import { Company } from '../user/models/company.model';
import { environment } from '../../../../../contract-frontend/src/environments/environment';
import { StorageData } from '../../common/models/storageData';
import { errorHandle } from '../../common/error-handling/errorHandling';
import { Store } from '../../store/store';
import { CompanyState } from '../user/models/company.state';
import {
  ADMIN_ROLES,
  DefaultAdminProfiles,
  StateStoreKeys,
  StatesService,
  UserRole,
  UserRolesPermissionsService,
  WorkspaceService,
} from '@socium/sociumflow';

const DEFAULT_STATE: CompanyState = {
  loaded: false,
  loading: false,
  error: false,
  currentPage: 0,
  data: [],
};

/**
 * This service handles authentication and authorization logic for the application.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService extends Store<CompanyState> {
  private readonly statesService = inject(StatesService);
  private readonly workspaceService = inject(WorkspaceService);
  private readonly userRolesPermissionsService = inject(
    UserRolesPermissionsService,
  );
  private readonly router = inject(Router);
  currentState!: CompanyState;

  constructor(private readonly httpClient: HttpClient) {
    super(httpClient, DEFAULT_STATE);
    this.getState().subscribe((state) => (this.currentState = state));
  }

  getToken(): string | undefined {
    const authData = this.getAuthData();
    return authData?._accessToken;
  }

  isAdmin(): boolean {
    return (
      this.isCompanyAdmin() || this.isSociumAdmin() || this.isHoldingAdmin()
    );
  }

  isPayrollAdmin(): boolean {
    const user = this.getAuthenticatedUser();
    if (!user) return false;

    const isAdminByRole = user.role
      ? ADMIN_ROLES.includes(user.role as unknown as UserRole)
      : false;

    const isAdminByProfile = this.hasCompanyProfile(
      DefaultAdminProfiles.ADMIN_PAYROLL,
    );
    return isAdminByRole || isAdminByProfile;
  }

  private hasCompanyProfile(profileName: DefaultAdminProfiles): boolean {
    const user = this.getAuthenticatedUser();
    const companyId = this.getCompany()?._id;
    if (!user || !companyId) return false;

    const profile = user.companyProfiles?.find(
      (p) => p.company?.toString() === companyId?.toString(),
    );

    return !!profile?.profiles?.some((p) => p.name === profileName);
  }

  isCompanyAdmin(): boolean {
    const user = this.getAuthenticatedUser();
    if (!user) return false;

    const isAdminByRole = user.role
      ? ADMIN_ROLES.includes(user.role as unknown as UserRole)
      : false;

    const isAdminByProfile = this.hasCompanyProfile(
      DefaultAdminProfiles.ADMIN_DOC,
    );

    return isAdminByRole || isAdminByProfile;
  }

  isEmployee(): boolean {
    const user = this.getAuthenticatedUser();
    if (!user) return false;

    const isEmployeeByRole = user.role === UserRole.EMPLOYEE;

    return isEmployeeByRole && !this.isAdmin();
  }

  isHoldingAdmin(): boolean {
    const user = this.getAuthenticatedUser();
    if (!user || !user.role) return false;
    return user.role === UserRole.HOLDING_ADMIN;
  }

  isSwitchCompanyDropdownVisible(): boolean {
    return this.isSociumAdmin() || this.isHoldingAdmin();
  }

  isSociumAdmin(): boolean {
    return this.userRolesPermissionsService.hasSociumManagementAccess();
  }

  getCompany(): Company | null {
    return this.statesService.getSelectedCompany() as unknown as Company | null;
  }

  getCompaniesThatHaveDocEnabled(): Observable<Company[]> {
    return this.httpClient
      .get<
        Company[]
      >(`${environment.apiUrl}/user/get-companies-with-doc-enabled`)
      .pipe(catchError(errorHandle));
  }

  getAuthenticatedUser(): User | null {
    const user = this.statesService.getCurrentUser() as unknown as User | null;
    if (user) return user;
    return null;
  }

  private getAuthData(): StorageData | null {
    return this.statesService.getCookie<StorageData>(StateStoreKeys.TOKEN_NAME);
  }

  async logout(): Promise<void> {
    await this.workspaceService.processLogout();
  }

  getTokenByTemporaryUUID(uuid: string) {
    return this.httpClient.get(
      `${environment.workSpaceEndpoint}/auth/check-uuid?uuid=${uuid}`,
      { responseType: 'text' },
    );
  }

  isTokenExpired() {
    const authData = this.statesService.getCookie<StorageData>(
      StateStoreKeys.TOKEN_NAME,
    );
    if (authData) {
      return (
        Date.now() >
        Date.parse(authData._tokenExpirationDate as unknown as string)
      );
    }
    return false;
  }

  updateAuthenticatedUser(user: User): void {
    this.statesService.setCurrentUser(
      user as unknown as Parameters<
        typeof this.statesService.setCurrentUser
      >[0],
    );
  }
}
